import { useRef } from 'react'

import { endOfDay, startOfDay } from 'date-fns'

import { ContentItemCard_ContentItem } from '~publish/components/ContentItemCard/fragments/ContentItemCard_ContentItem'
import { PostCard_Post } from '~publish/components/PostCard'
import { readFragment } from '~publish/graphql'
import { isOfType } from '~publish/helpers/typeGuards'

import type { BoardColumnItem } from './BoardColumn'
import type { BoardDateRange } from './useBoardState'
import { useContentsList } from '../PostList/useContentsList'
import type { PostStatus } from '../PostList/usePaginatedPostList'
import type { UseIntersectionObserverReturn } from '~publish/hooks/useIntersectionObserver'

// `ContentsConnection.totalCount` stops here, and 2000 means "2000 or more".
const CONTENTS_TOTAL_COUNT_CAP = 2000

export type UseBoardTagContentsColumnOptions = {
  channelIds?: string[]
  tagIds?: string[]
  statuses?: PostStatus[]
  /** The Board's date range. It widens to whole days, as `useBoardColumnPosts` does. */
  dateRange?: BoardDateRange
}

export type UseBoardTagContentsColumnResult = {
  items: BoardColumnItem[]
  loading: boolean
  fetchingMore: boolean
  reloading: boolean
  error: unknown
  hasNextPage: boolean
  /** Real server-side count — `contents` exposes `totalCount`, unlike the
   * two-connection merge this replaces. */
  totalCount: number
  /** True at the server's cap, where the count means "or more". */
  countIsApproximate: boolean
  lastElementRef: UseIntersectionObserverReturn[0]
}

/**
 * Returns one Tag column's posts in every status and its channel-less content
 * items, filtered by tag on the server. `drafts` cannot serve this, because it
 * returns draft posts only.
 */
export function useBoardTagContentsColumn({
  channelIds,
  tagIds,
  statuses,
  dateRange,
}: UseBoardTagContentsColumnOptions): UseBoardTagContentsColumnResult {
  const date = dateRange
    ? {
        start: startOfDay(dateRange.startDate).toISOString(),
        end: endOfDay(dateRange.endDate).toISOString(),
      }
    : undefined

  // Unlike the Draft column, a Tag column's channel-less content items are
  // never narrowed by the Channels filter — channel-less is its own
  // dimension, not something a channel selection excludes (see
  // getVisibleContentItems in BoardColumns.tsx for the existing rationale).
  const { data, loading, fetchingMore, reloading, error, lastElementRef } =
    useContentsList({
      channelIds,
      includeChannellessContent: true,
      tagIds,
      statuses,
      date,
    })

  const connection = data?.contents
  const lastKnownConnectionRef = useRef(connection)
  if (connection) {
    lastKnownConnectionRef.current = connection
  }
  const resolvedConnection = connection ?? lastKnownConnectionRef.current
  const edges = resolvedConnection?.edges ?? []
  const items: BoardColumnItem[] = edges.flatMap((edge): BoardColumnItem[] => {
    if (isOfType(edge.contentItemNode, 'ContentItem')) {
      return [
        {
          kind: 'contentItem',
          id: edge.contentItemNode.id,
          item: readFragment(ContentItemCard_ContentItem, edge.contentItemNode),
        },
      ]
    }
    if (isOfType(edge.postNode, 'Post')) {
      return [
        {
          kind: 'post',
          id: edge.postNode.id,
          post: readFragment(PostCard_Post, edge.postNode),
        },
      ]
    }
    return []
  })

  const totalCount = resolvedConnection?.totalCount ?? 0

  return {
    items,
    loading,
    fetchingMore,
    reloading,
    error,
    hasNextPage: resolvedConnection?.pageInfo.hasNextPage ?? false,
    totalCount,
    countIsApproximate: totalCount >= CONTENTS_TOTAL_COUNT_CAP,
    lastElementRef,
  }
}
